import React, { createContext, useContext, useState, useEffect } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db, googleProvider } from './firebase';
import { getUserPlan } from './adminConfig';
import { getCloudFunctionUrl } from './stripeConfig';

const AuthContext = createContext({});

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);

  // Sign up function
  const signup = async (email, password, displayName, selectedCountry = 'US') => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);

      // Update user profile with display name
      if (displayName) {
        await updateProfile(userCredential.user, {
          displayName: displayName
        });
      }

      // Create user profile in Firestore with selected country
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        uid: userCredential.user.uid,
        email: email,
        displayName: displayName || email.split('@')[0],
        plan: 'free',
        country: selectedCountry, // Save selected country
        createdAt: new Date().toISOString(),
        onboardingCompleted: false, // ⭐ NEW: Track onboarding status
        portfolioData: [
          { name: 'Stocks', value: 0, allocation: 0, color: '#3b82f6' },        // Blue
          { name: 'Bonds', value: 0, allocation: 0, color: '#8b5cf6' },          // Purple
          { name: 'Real Estate', value: 0, allocation: 0, color: '#10b981' },    // Emerald green
          { name: 'Commodities', value: 0, allocation: 0, color: '#facc15' },    // Bright yellow (gold)
          { name: 'Cryptocurrency', value: 0, allocation: 0, color: '#ec4899' }, // Pink/magenta
          { name: 'Cash', value: 0, allocation: 0, color: '#fb923c' }            // Coral orange
        ],
        investments: [],
        retirementGoals: {
          currentAge: 30,
          retirementAge: 65,
          currentSavings: 0,
          monthlyContribution: 0,
          desiredIncome: 50000,
          expectedReturn: 7
        }
      });

      // ✅ NEW: Send welcome email to user and notification to support
      try {
        console.log('[Signup] Sending welcome email to:', email);
        const response = await fetch(getCloudFunctionUrl('sendWelcomeEmailOnSignup'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            email: email,
            name: displayName || email.split('@')[0],
            uid: userCredential.user.uid
          })
        });

        if (response.ok) {
          console.log('[Signup] ✅ Welcome email sent successfully');
        } else {
          console.error('[Signup] ⚠️ Welcome email failed:', await response.text());
        }
      } catch (emailError) {
        console.error('[Signup] ⚠️ Error sending welcome email:', emailError);
        // Don't fail signup if email fails
      }

      return userCredential.user;
    } catch (error) {
      throw error;
    }
  };

  // Login function
  const login = (email, password) => {
    return signInWithEmailAndPassword(auth, email, password);
  };

  // Google sign-in function
  const signInWithGoogle = async (selectedCountry = 'US') => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;

      // Check if user profile exists, if not create it
      const docRef = doc(db, 'users', user.uid);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        // Create user profile for new Google sign-in user with selected country
        await setDoc(docRef, {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName || user.email.split('@')[0],
          photoURL: user.photoURL || null,
          plan: 'free',
          country: selectedCountry, // Save selected country
          createdAt: new Date().toISOString(),
          onboardingCompleted: false, // ⭐ NEW: Track onboarding status
          portfolioData: [
            { name: 'Stocks', value: 0, allocation: 0, color: '#3b82f6' },        // Blue
            { name: 'Bonds', value: 0, allocation: 0, color: '#8b5cf6' },          // Purple
            { name: 'Real Estate', value: 0, allocation: 0, color: '#10b981' },    // Emerald green
            { name: 'Commodities', value: 0, allocation: 0, color: '#facc15' },    // Bright yellow (gold)
            { name: 'Cryptocurrency', value: 0, allocation: 0, color: '#ec4899' }, // Pink/magenta
            { name: 'Cash', value: 0, allocation: 0, color: '#fb923c' }            // Coral orange
          ],
          investments: [],
          retirementGoals: {
            currentAge: 30,
            retirementAge: 65,
            currentSavings: 0,
            monthlyContribution: 0,
            desiredIncome: 50000,
            expectedReturn: 7
          }
        });

        // ✅ NEW: Send welcome email to user and notification to support
        try {
          console.log('[Google Signup] Sending welcome email to:', user.email);
          const response = await fetch(getCloudFunctionUrl('sendWelcomeEmailOnSignup'), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              email: user.email,
              name: user.displayName || user.email.split('@')[0],
              uid: user.uid
            })
          });

          if (response.ok) {
            console.log('[Google Signup] ✅ Welcome email sent successfully');
          } else {
            console.error('[Google Signup] ⚠️ Welcome email failed:', await response.text());
          }
        } catch (emailError) {
          console.error('[Google Signup] ⚠️ Error sending welcome email:', emailError);
          // Don't fail signup if email fails
        }
      }

      return user;
    } catch (error) {
      throw error;
    }
  };

  // Logout function
  const logout = () => {
    return signOut(auth);
  };

  // Reset password function
  const resetPassword = (email) => {
    return sendPasswordResetEmail(auth, email);
  };

  // Load user profile from Firestore
  const loadUserProfile = async (uid) => {
    try {
      const docRef = doc(db, 'users', uid);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const profileData = docSnap.data();

        // ✅ SECURITY: Check admin status from Firestore (not hardcoded emails)
        const isAdmin = profileData.isAdmin === true;

        // Admins automatically get premium access
        const effectivePlan = getUserPlan(isAdmin, profileData.plan);

        // If admin and not already premium in database, log it
        if (isAdmin && effectivePlan !== profileData.plan) {
          console.log('👑 Admin access granted:', profileData.email);
        }

        // Set profile with effective plan
        const enhancedProfile = {
          ...profileData,
          plan: effectivePlan,
          isAdmin: isAdmin
        };

        setUserProfile(enhancedProfile);
        return enhancedProfile;
      } else {
        console.log('No user profile found');
        return null;
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
      return null;
    }
  };

  // Update user profile in Firestore
  const updateUserProfile = async (uid, data) => {
    try {
      const docRef = doc(db, 'users', uid);
      await setDoc(docRef, data, { merge: true });
      setUserProfile(prev => ({ ...prev, ...data }));
      return true;
    } catch (error) {
      console.error('Error updating user profile:', error);
      return false;
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        await loadUserProfile(user.uid);
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userProfile,
    signup,
    login,
    signInWithGoogle,
    logout,
    resetPassword,
    loadUserProfile,
    updateUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
